/**
 * ShopsMap — real 2GIS MapGL map for the Shops finder (design revamp).
 *
 * Renders one marker per seller that has coordinates, centered on Bishkek;
 * clicking a marker selects that seller (opens the detail sheet). Loads the
 * MapGL SDK from the 2GIS CDN via a programmatic <script> append (no inline
 * script — CSP-safe), mirroring `features/marketplace/SellerMap`. 2GIS is the
 * ONLY map provider in GardEn (TRD §5.3 — never Mapbox/Google).
 *
 * The parent (`ShopsScreen`) only renders this when a MapGL key is baked in
 * (`VITE_2GIS_API_KEY`); otherwise it keeps the `MapPlaceholder` SVG stand-in.
 * If the SDK fails to load, this surfaces a lightweight error tile.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { LngLat, MapGLMap, MapGLMarker } from "@/types/2gis";
import type { SellerSummary } from "@/lib/garden-api";

/** 2GIS MapGL CDN entry point (allow-listed in the map CSP). */
const SDK_URL = "https://mapgl.2gis.com/api/js/v1";
/** Bishkek center + a zoom that frames the seeded markets (Dordoi/Alamedin/Orto-Say). */
const BISHKEK_CENTER: LngLat = [74.6, 42.87];
const CITY_ZOOM = 11;
/** Zoom level used to focus a selected seller's pin (SCRUM-147 follow-up) —
 * street/neighborhood level, a step in from the city-wide default. */
const SELLER_FOCUS_ZOOM = 15;
/** Animation duration for the pan/zoom-to-selection transition, in ms. */
const FOCUS_ANIMATION_MS = 400;

interface SellerCoord {
  seller: SellerSummary;
  lng: number;
  lat: number;
}

/** Keep only sellers with finite coordinates; coerce Decimal-as-string. */
function withCoords(sellers: SellerSummary[]): SellerCoord[] {
  const out: SellerCoord[] = [];
  for (const seller of sellers) {
    if (seller.lat === null || seller.lng === null) continue;
    const lat = Number(seller.lat);
    const lng = Number(seller.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      out.push({ seller, lat, lng });
    }
  }
  return out;
}

export interface ShopsMapProps {
  sellers: SellerSummary[];
  /** Called with the seller whose marker was clicked (opens the detail sheet). */
  onSelect: (seller: SellerSummary) => void;
  /**
   * Called once when the SDK cannot be loaded or the map cannot be built.
   * When supplied, the parent owns the failure surface (`ShopsMapPanel` swaps
   * to the locator plan and offers a retry); this component then renders
   * nothing rather than a second, competing error tile. Without it the
   * component still renders its own minimal tile, so it stays usable stand-alone.
   */
  onError?: () => void;
  height: number | string;
  /** The currently open seller's id (detail sheet), or `null`/`undefined`
   * when none is open. The map pans + zooms to focus this seller's pin
   * (SCRUM-147 follow-up) — set from whichever selection source (map
   * marker click or list-item click) opened the sheet. */
  selectedSellerId?: string | null;
}

export function ShopsMap({
  sellers,
  onSelect,
  onError,
  height,
  selectedSellerId,
}: ShopsMapProps): ReactElement {
  const { t } = useTranslation("shops");
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Kept in a ref so the load effect never re-runs (and re-appends the SDK
  // <script>) just because the parent handed us a fresh closure.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const mapRef = useRef<MapGLMap | null>(null);
  const markersRef = useRef<MapGLMarker[]>([]);
  // Keep the latest onSelect without re-running the marker effect.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  // Set right before onSelect fires from a MARKER click (never from the
  // seller list) — the focus effect below consumes it once to skip its own
  // pan/zoom. A pin the user just clicked is already on-screen; snapping the
  // camera to "frame" it is disorienting, especially after the user has
  // manually panned/zoomed to find it. Selecting the SAME seller again later
  // (e.g. from the list) still focuses normally — this only suppresses the
  // one focus effect run immediately triggered by the marker click itself.
  const skipNextFocusForRef = useRef<string | null>(null);

  const [ready, setReady] = useState(false);
  const [errored, setErrored] = useState(false);

  // Load the SDK (once) and initialize the map.
  useEffect(() => {
    let cancelled = false;
    const apiKey = import.meta.env.VITE_2GIS_API_KEY;

    /** One failure path: local tile state AND the parent's fallback hook. */
    const fail = (): void => {
      if (cancelled) return;
      setErrored(true);
      onErrorRef.current?.();
    };

    const initMap = (): void => {
      const mapgl = window.mapgl;
      const container = containerRef.current;
      if (!mapgl || !container || cancelled || mapRef.current) return;
      try {
        mapRef.current = new mapgl.Map(container, {
          center: BISHKEK_CENTER,
          zoom: CITY_ZOOM,
          ...(typeof apiKey === "string" && apiKey.length > 0
            ? { key: apiKey }
            : {}),
        });
        if (!cancelled) setReady(true);
        if (!cancelled && typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(() => {
            window.dispatchEvent(new Event("resize"));
          });
          resizeObserver.observe(container);
        }
      } catch {
        fail();
      }
    };

    // SCRUM-147: MapGL measures its canvas from the container's layout size
    // at construction time. On first mount the flex/grid container hasn't
    // resolved its final height yet (`height` prop is applied via inline
    // style, but the PARENT grid/flex box's own height settles a tick
    // later), so the map initializes against a zero/interim size and never
    // re-fetches tiles on its own — only an unrelated re-render (e.g.
    // opening the seller sheet) happened to trigger a native window resize
    // that MapGL does listen for. Watch the container directly and dispatch
    // a synthetic `resize` event whenever its box actually changes size —
    // covers the initial-layout-not-ready case AND real breakpoint/rotation
    // resizes, without depending on an SDK-specific resize method (the
    // hand-written `MapGLMap` type only covers what this component uses).
    let resizeObserver: ResizeObserver | null = null;

    const cleanup = (): void => {
      cancelled = true;
      resizeObserver?.disconnect();
      resizeObserver = null;
      for (const m of markersRef.current) m.destroy();
      markersRef.current = [];
      mapRef.current?.destroy();
      mapRef.current = null;
    };

    if (window.mapgl) {
      initMap();
      return cleanup;
    }
    const script = document.createElement("script");
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => {
      if (!cancelled) initMap();
    };
    script.onerror = () => {
      fail();
    };
    document.head.appendChild(script);
    return () => {
      script.onload = null;
      script.onerror = null;
      cleanup();
    };
  }, []);

  // (Re)build markers once the map is ready and whenever the seller set changes.
  useEffect(() => {
    const map = mapRef.current;
    const mapgl = window.mapgl;
    if (!ready || !map || !mapgl) return;
    for (const m of markersRef.current) m.destroy();
    markersRef.current = [];
    for (const { seller, lat, lng } of withCoords(sellers)) {
      const marker = new mapgl.Marker(map, { coordinates: [lng, lat] });
      marker.on("click", () => {
        skipNextFocusForRef.current = seller.id;
        onSelectRef.current(seller);
      });
      markersRef.current.push(marker);
    }
  }, [ready, sellers]);

  // Focus the selected seller's pin (SCRUM-147 follow-up): pan + zoom in
  // whenever selectedSellerId changes — EXCEPT when that selection just came
  // from clicking the marker itself (skipNextFocusForRef, set synchronously
  // in the marker's click handler above): that pin is already on-screen, so
  // moving the camera to it is redundant and disorienting, especially after
  // the user has manually panned/zoomed to find it. Selecting via the list
  // (or a deep link) always focuses normally. No-op when nothing is
  // selected — the map simply stays wherever the user last left it (closing
  // the detail sheet does not snap back to the city-wide view).
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !selectedSellerId) return;
    if (skipNextFocusForRef.current === selectedSellerId) {
      skipNextFocusForRef.current = null;
      return;
    }
    const match = withCoords(sellers).find(
      ({ seller }) => seller.id === selectedSellerId,
    );
    if (!match) return;
    map.setCenter([match.lng, match.lat], {
      animate: true,
      duration: FOCUS_ANIMATION_MS,
    });
    map.setZoom(SELLER_FOCUS_ZOOM, {
      animate: true,
      duration: FOCUS_ANIMATION_MS,
    });
  }, [ready, sellers, selectedSellerId]);

  // With `onError` wired the parent replaces this whole surface with the
  // locator plan + a retry, so rendering a second error tile here would just
  // flash a competing message during the swap.
  if (errored) {
    if (onError) return <div style={{ height }} />;
    return (
      <div
        data-testid="shops-map-error"
        role="alert"
        style={{ height }}
        className="grid w-full place-items-center bg-[var(--g-sunken)] px-4 text-center text-[13px] text-[var(--g-ink-2)]"
      >
        {t("map.failedTitle")}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="shops-map"
      style={{ height }}
      className="w-full overflow-hidden bg-[var(--g-sunken)]"
    />
  );
}

export default ShopsMap;
